import { Palmtree, Mountain, Waves, Sun, Anchor, Ship, Snowflake, Globe } from 'lucide-react';

/**
 * @fileoverview Type definitions for the salary simulator application
 * 
 * This module contains all TypeScript interfaces and types used throughout
 * the application for healthcare professions, salary calculations, primes,
 * and other core data structures.
 * 
 * @author App'Ines Team
 * @version 2.0.0
 * @since 2025
 */

/**
 * Represents a healthcare profession with its characteristics and default values
 * 
 * @interface Profession
 * @property {string} id - Unique identifier for the profession (e.g., 'infirmier', 'infirmiere-anesthesiste-iade')
 * @property {string} name - Display name of the profession in French
 * @property {string} category - Professional category (e.g., 'Paramédical', 'Médical')
 * @property {string} [path] - URL path for the profession (optional, undefined for default IDE profession)
 * @property {string} supabaseTable - Name of the Supabase table containing the salary grid for this profession
 * @property {number} averageConsultationPrice - Average consultation/service price in euros
 * @property {number} averageActsPerWeek - Average number of acts/hours per week
 * @property {number} defaultRetrocessionPercentage - Default retrocession percentage for this profession
 * 
 * @example
 * const infirmier: Profession = {
 *   id: 'infirmier',
 *   name: 'Infirmier Diplômé d\'État (IDE)',
 *   category: 'Paramédical',
 *   supabaseTable: 'grille_Hopital_IDE',
 *   averageConsultationPrice: 30.6,
 *   averageActsPerWeek: 35,
 *   defaultRetrocessionPercentage: 0
 * };
 * 
 * @example
 * const iade: Profession = {
 *   id: 'infirmiere-anesthesiste-iade',
 *   name: 'Infirmière Anesthésiste (IADE)',
 *   category: 'Paramédical Spécialisé',
 *   path: '/infirmiere-anesthesiste-iade',
 *   supabaseTable: 'grille_Hopital_IADE',
 *   averageConsultationPrice: 35.0,
 *   averageActsPerWeek: 35,
 *   defaultRetrocessionPercentage: 0
 * };
 */
export interface Profession {
  id: string;
  name: string;
  category: string;
  path?: string; // Optional - undefined for default IDE profession, specific path for specializations
  supabaseTable: string; // Name of the Supabase table containing the salary grid
  averageConsultationPrice: number;
  averageActsPerWeek: number;
  defaultRetrocessionPercentage: number;
}

/**
 * Represents activity data for a healthcare professional
 */
export interface ActivityData {
  profession: Profession | null;
  actsPerWeekMin: number;
  actsPerWeekMax: number;
  averageConsultationPrice: number;
}

/**
 * Represents vacation data for revenue calculations
 */
export interface VacationData {
  vacationWeeks: number;
  publicHolidays: number;
  replacementWeeks: number;
  replacementPercentage: number;
}

/**
 * Represents professional status data
 */
export type StatusType = 'titulaire' | 'stagiaire' | 'contractuel' | 'liberal' | 'remplacant';
export type FiscalRegime = 'reel' | 'micro';

export interface StatusData {
  type: StatusType;
  retrocessionPercentage: number;
  retrocessionCap: number | null;
  fiscalRegime: FiscalRegime;
}

export interface StatusOption {
  value: StatusType;
  label: string;
  description: string;
}

/**
 * Represents user interaction tracking
 */
export interface UserInteractions {
  hasModifiedActivity: boolean;
  hasModifiedVacation: boolean;
  hasModifiedCharges: boolean;
  hasViewedCharges: boolean;
  hasModifiedPrimes: boolean;
}

/**
 * Represents a todo item in the configuration checklist
 */
export interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  completedAt?: number;
}

/**
 * Overseas territory identifiers for salary calculations
 */
export type OverseasTerritoryId = 
  | 'guadeloupe' 
  | 'guyane' 
  | 'martinique' 
  | 'reunion' 
  | 'saint-barthelemy' 
  | 'saint-martin' 
  | 'saint-pierre-miquelon' 
  | 'mayotte'
  | null;

/**
 * Represents an overseas territory with all its characteristics
 * 
 * @interface OverseasTerritory
 * @property {OverseasTerritoryId} id - Unique identifier for the territory (null for metropolitan France)
 * @property {string} name - Display name of the territory in French
 * @property {string} path - URL path segment for the territory
 * @property {number} majoration - Salary majoration percentage
 * @property {number} [complement] - Optional temporary complement percentage
 * @property {number} [abatementRate] - Optional tax abatement rate percentage
 * @property {number} [abatementCap] - Optional tax abatement cap in euros
 * @property {any} icon - Lucide React icon component
 * 
 * @example
 * const guadeloupe: OverseasTerritory = {
 *   id: 'guadeloupe',
 *   name: 'Guadeloupe',
 *   path: 'guadeloupe',
 *   majoration: 25,
 *   complement: 15,
 *   abatementRate: 30,
 *   abatementCap: 2450,
 *   icon: Palmtree
 * };
 */
export interface OverseasTerritory {
  id: OverseasTerritoryId;
  name: string;
  path: string;
  majoration: number;
  complement?: number;
  abatementRate?: number;
  abatementCap?: number;
  icon: any;
}

/**
 * Complete overseas territories data with all characteristics
 * Includes metropolitan France (null id) for consistency
 */
export const OVERSEAS_TERRITORIES_DATA: OverseasTerritory[] = [
  {
    id: null,
    name: 'France métropolitaine',
    path: 'france',
    majoration: 0,
    icon: Globe
  },
  {
    id: 'guadeloupe',
    name: 'Guadeloupe',
    path: 'guadeloupe',
    majoration: 25,
    complement: 15,
    abatementRate: 30,
    abatementCap: 2450,
    icon: Palmtree
  },
  {
    id: 'guyane',
    name: 'Guyane',
    path: 'guyane',
    majoration: 25,
    complement: 15,
    abatementRate: 40,
    abatementCap: 4050,
    icon: Mountain
  },
  {
    id: 'martinique',
    name: 'Martinique',
    path: 'martinique',
    majoration: 25,
    complement: 15,
    abatementRate: 30,
    abatementCap: 2450,
    icon: Waves
  },
  {
    id: 'reunion',
    name: 'La Réunion',
    path: 'reunion',
    majoration: 25,
    complement: 10,
    abatementRate: 30,
    abatementCap: 2450,
    icon: Sun
  },
  {
    id: 'saint-barthelemy',
    name: 'Saint-Barthélemy',
    path: 'saint-barthelemy',
    majoration: 25,
    complement: 15,
    icon: Ship
  },
  {
    id: 'saint-martin',
    name: 'Saint-Martin',
    path: 'saint-martin',
    majoration: 25,
    complement: 15,
    icon: Waves
  },
  {
    id: 'saint-pierre-miquelon',
    name: 'Saint-Pierre-et-Miquelon',
    path: 'saint-pierre-miquelon',
    majoration: 25,
    icon: Snowflake
  },
  {
    id: 'mayotte',
    name: 'Mayotte',
    path: 'mayotte',
    majoration: 40,
    abatementRate: 40,
    abatementCap: 4050,
    icon: Anchor
  }
];

/**
 * Helper function to find an overseas territory by its ID
 * 
 * @param {OverseasTerritoryId} territoryId - The territory ID to search for
 * @returns {OverseasTerritory | undefined} The found territory or undefined
 * 
 * @example
 * const guadeloupe = findOverseasTerritoryById('guadeloupe');
 * console.log(guadeloupe?.name); // 'Guadeloupe'
 */
export function findOverseasTerritoryById(territoryId: OverseasTerritoryId): OverseasTerritory | undefined {
  return OVERSEAS_TERRITORIES_DATA.find(t => t.id === territoryId);
}

/**
 * Helper function to find an overseas territory by its URL path
 * 
 * @param {string} path - The URL path to search for
 * @returns {OverseasTerritory | undefined} The found territory or undefined
 * 
 * @example
 * const guadeloupe = findOverseasTerritoryByPath('guadeloupe');
 * console.log(guadeloupe?.name); // 'Guadeloupe'
 */
export function findOverseasTerritoryByPath(path: string): OverseasTerritory | undefined {
  return OVERSEAS_TERRITORIES_DATA.find(t => t.path === path);
}

/**
 * Helper function to get all overseas territories (excluding metropolitan France)
 * 
 * @returns {OverseasTerritory[]} Array of overseas territories
 */
export function getOverseasTerritories(): OverseasTerritory[] {
  return OVERSEAS_TERRITORIES_DATA.filter(t => t.id !== null);
}

/**
 * Represents salary grid data structures
 */
export interface EchelonData {
  echelon: number;
  indice?: number; // Optional for backward compatibility
  indiceMajoré: number;
  dureeAvancement?: string;
  salaireAnnuelBrut?: number;
  salaireNetMensuel?: number;
  echelonLabel?: string; // Label for display (especially for negative echelons)
  echelonRangeLabel?: string; // Label for range slider display
}

export interface GradeData {
  id: number;
  nom: string;
  description: string;
  echelons: EchelonData[];
}

/**
 * Raw data structure from Supabase salary grid tables
 */
export interface GrilleHopitalIDERow {
  id: number;
  created_at: string;
  grade_id: number;
  grade_nom: string;
  grade_description: string;
  echelon: number;
  indice_brut: number;
  indice_majore: number;
  duree_avancement: string;
  salaire_annuel_brut: number;
  salaire_net_mensuel: number;
  no_use?: string;
  echelon_label?: string;
  echelon_range_label?: string;
}

/**
 * Prime and allowance data structures
 */
export interface PrimeIndemnite {
  id: string;
  nom: string;
  description: string;
  montantDefaut: number;
  montantUtilisateur: number;
  active: boolean;
  type: 'fixe' | 'variable' | 'pourcentage';
  icon: any;
  color: string;
  details?: string;
  calculable?: boolean;
  calculDetails?: string;
  availableForProfessions: string[];
  proratisee?: boolean;
  formuleCode?: string;
  urlDoc?: string;
  ordre?: number;
  eligibleEchelons?: number[] | null;
  primeType?: string;
  
  // Negotiation flag for contractual employees
  aNegocier?: boolean;
  
  // CTI-specific properties
  ctiPoints?: number;
  
  // Service prime properties
  serviceTauxMax?: number;
  isMaxCalculated?: boolean;
  
  // Transport-specific properties
  montantBase?: number;
  pourcentageRemboursement?: number;
  
  // IFTD configuration
  iftdConfig?: {
    dimanchesParMois: number;
    joursFeriesTravailles: string[];
  };
  
  // ISS configuration
  issConfig?: {
    echelonHistorique: number;
    indiceMajoreHistorique: number;
  };
  
  // Contagion configuration
  contagionConfig?: {
    joursTravaillesParAn: number;
  };
  
  // Prime nuit configuration
  primeNuitConfig?: {
    nuitsParMois: number;
  };
  
  // SFT configuration
  sftConfig?: {
    nombreEnfants: number;
  };
  
  // Insalubrité cat 2 configuration
  montantDemiJourneeInsalubrite?: number;
  insalubriteCat2Config?: {
    joursTravaillesParMois: number;
  };
  
  // NBI configuration
  nbiConfig?: {
    points: number;
  };
  
  // Eligibility by status
  eligibleStatuses?: StatusType[];

  // Références légales (provenance Supabase : colonnes base_legale, decret_reference, date_revalorisation, note_calcul)
  baseLegale?: string;
  decretReference?: string;
  dateRevalorisation?: string;
  noteCalcul?: string;
}

export interface PrimesData {
  primes: PrimeIndemnite[];
  totalMensuel: number;
}

/**
 * Social contribution data structures
 */
export interface CotisationSalarialeRow {
  id: number;
  created_at: string;
  cotisation_name: string;
  cotisations_detail: string;
  assiette: string;
  percentage_assiette_low: number;
  percentage_assiette_high: number;
  plafond_TIBM: number;
  rate: number;
  rate_non_deductible: number;
}

export interface CotisationSalariale {
  id: string;
  nom: string;
  detail: string;
  assiette: string;
  tauxBas: number;
  tauxHaut: number;
  plafondTIBM: number;
  taux: number;
  tauxNonDeductible: number;
}

export interface CalculatedCotisation {
  nom: string;
  montantMensuel: number;
  montantAnnuel: number;
  detail: string;
  calculDetail: string;
  tauxApplique: number;
  assietteMontant: number;
}

export interface CotisationsData {
  cotisations: CotisationSalariale[];
  totalMensuel: number;
  totalAnnuel: number;
}

/**
 * Revenue calculation data structures
 */
export interface RevenueCalculation {
  grossAnnualRevenue: number;
  grossMonthlyRevenue: number;
  netAnnualRevenue: number;
  netMonthlyRevenue: number;
  chargesAmount: number;
  taxAmount: number;
  effectiveTaxRate: number;
}

/**
 * Charges data structure
 */
export interface ChargesData {
  chargesPercentage: number;
}